import { useEffect, useRef, useState } from "react";
import { auth, db } from "./firebase";

import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  setPersistence,
  browserSessionPersistence
} from "firebase/auth";

import {
  doc,
  getDoc
} from "firebase/firestore";

import Dashboard from "./Dashboard";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import logo from "./Logo.png";
import {
  setUserOffline,
  startUserPresence
} from "./services/presenceService";

const SESSION_TIMEOUT_MS = 60 * 60 * 1000;
const LAST_ACTIVITY_STORAGE_KEY = "ecalpLastActivityAt";

function App() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginError, setLoginError] = useState("");
  const [loginMessageType, setLoginMessageType] = useState("error");
  const sessionTimeoutRef = useRef(null);

  useEffect(() => {
    let unsubscribe = () => {};
    let isMounted = true;

    const initializeAuth = async () => {
      await setPersistence(auth, browserSessionPersistence);

      if (!isMounted) {
        return;
      }

      unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        try {
          const lastActivityAt = Number(
            sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) || 0
          );

          if (lastActivityAt && Date.now() - lastActivityAt >= SESSION_TIMEOUT_MS) {
            await setUserOffline(currentUser.uid);
            await signOut(auth);

            sessionStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
            setUser(null);
            setLoginMessageType("error");
            setLoginError("Güvenliğiniz için 60 dakika işlem yapılmadığı için oturumunuz kapatıldı.");
            setUserRole(null);
            setUserData(null);
            setAuthLoading(false);

            return;
          }

          if (!lastActivityAt) {
            sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
          }

          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userRef);

          // Kullanıcı kaydı yoksa çıkış yap
          if (!userSnap.exists()) {
            console.error("Kullanıcı yetki kaydı bulunamadı.");

            await signOut(auth);

            setUserRole(null);
            setAuthLoading(false);

            return;
          }

          const userData = userSnap.data();

          // Kullanıcı pasif ise çıkış yap
          if (userData.isActive === false) {
            console.error("Kullanıcı pasif.");

            await signOut(auth);

            setUserRole(null);
            setUserData(null);
            setAuthLoading(false);

            return;
          }

          setUserRole(userData.role || "viewer");
          setUserData(userData);
        } catch (error) {
          console.error("Yetki kontrol hatası:", error);

          await signOut(auth);

          setUserRole(null);
        }
      } else {
        setUserRole(null);
        setUserData(null);
      }

      setAuthLoading(false);
      });
    };

    initializeAuth().catch(async (error) => {
      console.error("Oturum başlatma hatası:", error);

      await signOut(auth);

      if (isMounted) {
        setUser(null);
        setUserRole(null);
        setUserData(null);
        setAuthLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user || !userData) return undefined;

    return startUserPresence({
      uid: user.uid,
      email: user.email,
      name: userData.name,
      role: userData.role || userRole,
      photoURL: userData.photoURL || user.photoURL
    });
  }, [user, userData, userRole]);

  useEffect(() => {
    if (!user) {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }

      return undefined;
    }

    const logoutDueToInactivity = async () => {
      await setUserOffline(user.uid);
      await signOut(auth);

      sessionStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
      setEmail("");
      setPassword("");
      setLoginMessageType("error");
      setLoginError("Güvenliğiniz için 60 dakika işlem yapılmadığı için oturumunuz kapatıldı.");
      setUserRole(null);
      setUserData(null);
    };

    const getLastActivityAt = () => Number(
      sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) || 0
    );

    const scheduleSessionTimeout = () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }

      const lastActivityAt = getLastActivityAt() || Date.now();
      const elapsedTime = Date.now() - lastActivityAt;
      const remainingTime = SESSION_TIMEOUT_MS - elapsedTime;

      if (remainingTime <= 0) {
        logoutDueToInactivity();
        return;
      }

      sessionTimeoutRef.current = setTimeout(
        logoutDueToInactivity,
        remainingTime
      );
    };

    const checkSessionTimeout = () => {
      const lastActivityAt = getLastActivityAt();

      if (lastActivityAt && Date.now() - lastActivityAt >= SESSION_TIMEOUT_MS) {
        logoutDueToInactivity();
        return;
      }

      scheduleSessionTimeout();
    };

    const resetSessionTimeout = () => {
      const lastActivityAt = getLastActivityAt();

      if (lastActivityAt && Date.now() - lastActivityAt >= SESSION_TIMEOUT_MS) {
        logoutDueToInactivity();
        return;
      }

      sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
      scheduleSessionTimeout();
    };

    const activityEvents = [
      "click",
      "keydown",
      "mousemove",
      "scroll",
      "touchstart"
    ];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetSessionTimeout, { passive: true });
    });

    window.addEventListener("focus", checkSessionTimeout);
    window.addEventListener("pageshow", checkSessionTimeout);
    document.addEventListener("visibilitychange", checkSessionTimeout);

    checkSessionTimeout();

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetSessionTimeout);
      });

      window.removeEventListener("focus", checkSessionTimeout);
      window.removeEventListener("pageshow", checkSessionTimeout);
      document.removeEventListener("visibilitychange", checkSessionTimeout);

      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }
    };
  }, [user]);

  const handleLogin = async () => {
    setLoginError("");

    try {
      await setPersistence(auth, browserSessionPersistence);

      await signInWithEmailAndPassword(
        auth,
        email.trim().toLowerCase(),
        password
      );
    } catch (error) {
      console.error(error);

      setLoginError(
        "E-posta adresi veya şifre hatalı. Lütfen tekrar deneyin."
      );
    }
  };

  
  const handlePasswordReset = async () => {
    setLoginError("");

    if (!email) {
      setLoginMessageType("error");
      setLoginError("Lütfen önce e-posta adresinizi giriniz.");
      return;
    }

    try {
      await sendPasswordResetEmail(
        auth,
        email.trim().toLowerCase()
      );

      setLoginMessageType("success");
      setLoginError(
        "Şifre belirleme / sıfırlama bağlantısı e-posta adresinize gönderildi."
      );
      } catch (error) {
      setLoginMessageType("error");
      setLoginError(
        "Bu e-posta adresi için kayıtlı kullanıcı bulunamadı."
      );
    }
  };

  const handleLogout = async () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }

    if (user?.uid) {
      await setUserOffline(user.uid);
    }

    await signOut(auth);

    sessionStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    setEmail("");
    setPassword("");
    setLoginError("");
    setUserRole(null);
    setUserData(null);
  };

  if (authLoading || (user && !userRole)) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f7fb",
          color: "#124254",
          fontWeight: 700
        }}
      >
        Yükleniyor...
      </div>
    );
  }

  if (user) {
    return (
      <div style={{ position: "relative" }}>
        <Dashboard
          user={user}
          userRole={userRole}
          userData={userData}
          onLogout={handleLogout}
          onUserDataUpdate={(nextUserData) => {
            setUserData((current) => ({
              ...(current || {}),
              ...(nextUserData || {})
            }));
          }}
        />
        <PwaInstallPrompt />
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }

            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(135deg, #124254 0%, #174d61 45%, #5eb8b2 100%)",
          padding: 24,
          position: "relative",
          overflow: "hidden"
        }}
      >
        <PwaInstallPrompt />
        <div
          style={{
            position: "absolute",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "rgba(94,184,178,0.18)",
            filter: "blur(90px)",
            top: -120,
            right: -120
          }}
        />

        <div
          style={{
            position: "absolute",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "rgba(18,66,84,0.22)",
            filter: "blur(100px)",
            bottom: -100,
            left: -100
          }}
        />

        <div
          style={{
            width: "100%",
            maxWidth: 420,
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: 28,
            padding: "42px 38px",
            boxShadow: "0 30px 80px rgba(0,0,0,0.28)",
            textAlign: "center",
            animation: "fadeIn 0.6s ease",
            position: "relative",
            zIndex: 2
          }}
        >
          <img
            src={logo}
            alt="e-cAlp Logo"
            style={{
              width: 180,
              marginBottom: 18,
              objectFit: "contain"
            }}
          />

          <h1
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 800,
              color: "#124254"
            }}
          >
            Proje Takip Paneli
          </h1>

          <p
            style={{
              marginTop: 8,
              marginBottom: 30,
              color: "#6b7280",
              fontSize: 15
            }}
          >
            e-cAlp Yazılım & Danışmanlık
          </p>

          <input
            type="email"
            placeholder="E-posta adresi"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setLoginError("");
            }}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "14px 16px",
              borderRadius: 14,
              border: "1px solid #dbe2ea",
              marginBottom: 14,
              fontSize: 15,
              outline: "none"
            }}
          />

          <input
            type="password"
            placeholder="Şifre"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setLoginError("");
            }}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "14px 16px",
              borderRadius: 14,
              border: "1px solid #dbe2ea",
              marginBottom: loginError ? 12 : 22,
              fontSize: 15,
              outline: "none"
            }}
          />

          {loginError && (
            <div
              style={{
                background: loginMessageType === "success" ? "#dcfce7" : "#fee2e2",
                color: loginMessageType === "success" ? "#166534" : "#991b1b",
                border: loginMessageType === "success" ? "1px solid #bbf7d0" : "1px solid #fecaca",
                padding: "10px 12px",
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 18,
                textAlign: "left"
              }}
            >
              {loginError}
            </div>
          )}

          <button
            onClick={handleLogin}
            style={{
              width: "100%",
              padding: "14px 18px",
              borderRadius: 14,
              border: "none",
              background: "#124254",
              color: "#ffffff",
              fontSize: 16,
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            Giriş Yap
          </button>

          <button
            onClick={handlePasswordReset}
            style={{
              marginTop: 14,
              width: "100%",
              background: "transparent",
              border: "none",
              color: "#124254",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            Şifremi Belirle / Unuttum
          </button>
        </div>
      </div>
    </>
  );
}

export default App;
